import React, { useState, useEffect } from 'react';
import { X, Phone, Video, ArrowUpRight, ArrowDownLeft, XCircle, Clock, Trash2 } from 'lucide-react';
import { ref, onValue, remove, off } from 'firebase/database';
import { db } from '../../../../integrations/firebase';
import { useChat } from '../../hooks/useChat';

const CallHistoryModal = ({ isOpen, onClose }) => {
    const { currentUser, startCall, allUsers } = useChat();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const resolveName = (log) => {
        if (!log.participants || !currentUser) return "Unknown";
        // Find first ID that isn't me
        const otherId = Array.isArray(log.participants)
            ? log.participants.find(uid => uid !== currentUser.uid)
            : null;

        if (!otherId) return "Unknown";

        const user = allUsers.find(u => u.id === otherId);
        return user ? user.name : "Unknown User";
    };

    useEffect(() => {
        if (!isOpen || !currentUser?.uid) return;

        setLoading(true);
        const logsRef = ref(db, `users/${currentUser.uid}/call_logs`);

        const handleLogs = (snapshot) => {
            const data = snapshot.val();
            if (data) {
                // Convert to array and sort desc
                const list = Object.entries(data).map(([key, val]) => ({
                    id: key,
                    ...val
                })).sort((a, b) => b.timestamp - a.timestamp);
                setLogs(list);
            } else {
                setLogs([]);
            }
            setLoading(false);
        };

        const unsub = onValue(logsRef, handleLogs);
        return () => {
            off(logsRef, 'value', unsub);
        };
    }, [isOpen, currentUser?.uid]);

    const clearHistory = async () => {
        if (window.confirm("Clear all call history?")) {
            await remove(ref(db, `users/${currentUser.uid}/call_logs`));
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();

        return isToday
            ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleCallback = (log) => {
        if (!log.participants) return;

        // Find the other participant
        // If 1:1, it's the other array entry or single target
        let targetId = null;
        if (Array.isArray(log.participants)) {
            targetId = log.participants.find(uid => uid !== currentUser.uid);
        } else {
            // Fallback if legacy data
            // If I was caller, target is participant. If I was receiver, target is caller.
            // Ideally logs should store 'otherPartyId' or we derive it.
        }

        // Simpler: The log should probably reference the other user for display purposes.
        // But let's assume participants array is available.
        // If group call, maybe just rejoin? (Complex)
        // For now, support 1:1 callback

        if (targetId) {
            startCall(targetId, 'voice'); // Default to voice or maybe match original type?
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-md h-[70vh] shadow-2xl scale-100 animate-in zoom-in-95 duration-200 mx-4 flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-slate-100">
                    <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Clock size={24} className="text-indigo-600" />
                        Call History
                    </h3>
                    <div className="flex gap-2">
                        {logs.length > 0 && (
                            <button onClick={clearHistory} className="text-red-400 hover:text-red-500 p-2 hover:bg-red-50 rounded-full" title="Clear History">
                                <Trash2 size={18} />
                            </button>
                        )}
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                    {loading ? (
                        <div className="flex justify-center p-8 text-slate-400">Loading...</div>
                    ) : logs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <Clock size={48} className="mb-4 opacity-20" />
                            <p>No recent calls</p>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {logs.map(log => {
                                // Determine Icon & Color
                                let Icon = ArrowUpRight;
                                let color = 'text-slate-500';
                                let text = 'Outgoing';

                                if (log.type === 'missed') {
                                    Icon = XCircle;
                                    color = 'text-red-500';
                                    text = 'Missed';
                                } else if (log.type === 'incoming') {
                                    Icon = ArrowDownLeft;
                                    color = 'text-emerald-500';
                                    text = 'Incoming';
                                } else if (log.type === 'outgoing') {
                                    Icon = ArrowUpRight;
                                    color = 'text-blue-500';
                                    text = 'Outgoing';
                                }

                                const isVideo = log.callType === 'video';

                                return (
                                    <div key={log.id} className="group flex items-center gap-3 p-3 hover:bg-slate-50 rounded-xl transition-colors">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-white border border-slate-100 shadow-sm ${color}`}>
                                            <Icon size={18} />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <h4 className="font-semibold text-slate-800 text-sm truncate">
                                                    {resolveName(log)}
                                                </h4>
                                                <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap ml-2">
                                                    {formatTime(log.timestamp)}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                                {isVideo ? <Video size={12} /> : <Phone size={12} />}
                                                <span>{text}</span>
                                                {log.duration && (
                                                    <span className="text-slate-300">• {log.duration}s</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                            {/* Callback buttons */}
                                            {/* Only simpler for now */}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CallHistoryModal;
